// src/components/DocumentViewerModal.jsx
// Modal fullscreen para visualizar PDFs con react-pdf - Versión compacta con scroll continuo

import React, { useState, useEffect, useRef } from 'react';
import { X, ZoomIn, ZoomOut, Loader } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useTranslation } from 'react-i18next';

// Configurar worker de PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * Modal para visualizar documentos PDF
 * Scroll continuo - todas las páginas visibles
 */
const DocumentViewerModal = ({ document: pdfDocument, onClose }) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const openTimeRef = useRef(Date.now());

  // Bloquear scroll del body
  useEffect(() => {
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.document.body.style.overflow = 'auto';
    };
  }, []);

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const handleDocumentLoadError = (err) => {
    console.error('Error cargando PDF:', err);
    setError(t('docViewer.loadError'));
    setLoading(false);
  };

  const handleClose = () => {
    const viewDuration = Math.floor((Date.now() - openTimeRef.current) / 1000);
    onClose(viewDuration);
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 lg:flex lg:items-center lg:justify-center lg:p-4">
        <div className="relative bg-slate-900 w-full h-full flex flex-col lg:rounded-2xl lg:shadow-2xl lg:max-w-5xl lg:max-h-[95vh] lg:border lg:border-slate-700 overflow-hidden">

          {/* Header compacto */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-800 flex-shrink-0">
            {/* Título */}
            <h2 className="text-sm font-medium text-white truncate flex-1 mr-2">
              {pdfDocument.name}
            </h2>

            {/* Controles en línea */}
            <div className="flex items-center gap-1">
              <button
                onClick={zoomOut}
                disabled={scale <= 0.5}
                className="p-1.5 hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                title={t('docViewer.zoomOut')}
              >
                <ZoomOut className="w-4 h-4 text-slate-300" />
              </button>
              <span className="text-xs text-slate-400 w-12 text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                disabled={scale >= 3}
                className="p-1.5 hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                title={t('docViewer.zoomIn')}
              >
                <ZoomIn className="w-4 h-4 text-slate-300" />
              </button>

              {/* Separador y cerrar */}
              <div className="w-px h-5 bg-slate-600 mx-1" />
              <button
                onClick={handleClose}
                className="p-1.5 hover:bg-slate-700 rounded transition-colors"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* PDF Viewer - Scroll continuo */}
          <div className="flex-1 overflow-auto bg-slate-950">
            {loading && (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <Loader className="w-8 h-8 text-amber-500 animate-spin" />
                <p className="text-slate-400 text-sm">{t('docViewer.loading')}</p>
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-center px-4">
                <div className="p-4 bg-red-500/10 rounded-full">
                  <X className="w-8 h-8 text-red-400" />
                </div>
                <p className="text-red-400 text-sm max-w-md">{error}</p>
                <button
                  onClick={handleClose}
                  className="mt-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors text-sm"
                >
                  {t('common.close')}
                </button>
              </div>
            )}

            {!error && (
              <Document
                file={pdfDocument.url}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading={null}
                className="flex flex-col items-center py-4 gap-4"
              >
                {numPages && Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    scale={scale}
                    loading={null}
                    className="shadow-xl"
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
              </Document>
            )}
          </div>

          {/* Footer mínimo - indicador de páginas */}
          {numPages && (
            <div className="px-3 py-1.5 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
              <p className="text-xs text-slate-500 text-center">
                {numPages} {numPages === 1 ? t('docViewer.page') : t('docViewer.pages')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
